/**
 * CanvasTabBar Component
 * Bottom tab bar for managing multiple diagram canvases
 * Features: Tab switching, add/remove tabs, rename tabs, unsaved indicator
 */

import { memo, useState, useCallback, useRef, useEffect } from 'react'
import { Plus, X, Edit2, Check, Copy, XCircle, ChevronsRight } from 'lucide-react'
import type { CanvasTab } from '../types/multiCanvas'
import { useModal } from './modals/ModalProvider'
import '../styles/canvas-tab-bar.css'

export interface CanvasTabBarProps {
  tabs: CanvasTab[]
  activeTabId: string
  onTabClick: (id: string) => void
  onTabClose: (id: string) => void
  onTabAdd: () => void
  onTabRename: (id: string, name: string) => void
  onTabReorder?: (startIndex: number, endIndex: number) => void
  onTabDuplicate?: (id: string) => void
  onCloseOthers?: (id: string) => void
  onCloseToRight?: (id: string) => void
}

function CanvasTabBar({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
  onTabAdd,
  onTabRename,
  onTabReorder,
  onTabDuplicate,
  onCloseOthers,
  onCloseToRight
}: CanvasTabBarProps) {
  const modal = useModal()
  const [editingTabId, setEditingTabId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [draggedTabId, setDraggedTabId] = useState<string | null>(null)
  const [dragOverTabId, setDragOverTabId] = useState<string | null>(null)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; tabId: string } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const contextMenuRef = useRef<HTMLDivElement>(null)

  // Focus input when editing starts
  useEffect(() => {
    if (editingTabId && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingTabId])

  // Start editing tab name
  const startEditing = useCallback((tab: CanvasTab, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingTabId(tab.id)
    setEditingName(tab.name)
  }, [])

  // Finish editing tab name
  const finishEditing = useCallback(() => {
    if (editingTabId && editingName.trim() !== '') {
      onTabRename(editingTabId, editingName.trim())
    }
    setEditingTabId(null)
    setEditingName('')
  }, [editingTabId, editingName, onTabRename])

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditingTabId(null)
    setEditingName('')
  }, [])

  // Handle key events during editing
  const handleEditKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      finishEditing()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEditing()
    }
  }, [finishEditing, cancelEditing])

  // Handle tab close with confirmation for unsaved changes
  const handleTabClose = useCallback((tab: CanvasTab, e: React.MouseEvent) => {
    e.stopPropagation()

    // Don't close if it's the last tab
    if (tabs.length === 1) {
      return
    }

    // Confirm if there are unsaved changes
    if (tab.unsavedChanges) {
      modal.confirm({
        message: `Close "${tab.name}"? You have unsaved changes.`,
        type: 'warning'
      }).then((confirmed) => {
        if (confirmed) {
          onTabClose(tab.id)
        }
      })
      return
    }

    onTabClose(tab.id)
  }, [tabs.length, onTabClose, modal])

  // Drag and drop handlers
  const handleDragStart = useCallback((tab: CanvasTab, e: React.DragEvent) => {
    setDraggedTabId(tab.id)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', tab.id)
    // Add a slight opacity to the dragged element
    const target = e.currentTarget as HTMLElement
    setTimeout(() => {
      target.style.opacity = '0.5'
    }, 0)
  }, [])

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement
    target.style.opacity = '1'
    setDraggedTabId(null)
    setDragOverTabId(null)
  }, [])

  const handleDragOver = useCallback((tab: CanvasTab, e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverTabId(tab.id)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOverTabId(null)
  }, [])

  const handleDrop = useCallback((tab: CanvasTab, e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!draggedTabId || !onTabReorder || draggedTabId === tab.id) {
      setDragOverTabId(null)
      return
    }

    const draggedIndex = tabs.findIndex(t => t.id === draggedTabId)
    const targetIndex = tabs.findIndex(t => t.id === tab.id)

    if (draggedIndex !== -1 && targetIndex !== -1 && draggedIndex !== targetIndex) {
      onTabReorder(draggedIndex, targetIndex)
    }

    setDragOverTabId(null)
  }, [draggedTabId, tabs, onTabReorder])

  // Context menu handlers
  const handleContextMenu = useCallback((tab: CanvasTab, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setContextMenu({ x: e.clientX, y: e.clientY, tabId: tab.id })
  }, [])

  const closeContextMenu = useCallback(() => {
    setContextMenu(null)
  }, [])

  // Close context menu when clicking outside
  useEffect(() => {
    if (!contextMenu) return

    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        closeContextMenu()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [contextMenu, closeContextMenu])

  return (
    <div className="canvas-tab-bar">
      {/* Tabs and Add Button */}
      <div className="canvas-tabs-scroll">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId
          const isEditing = editingTabId === tab.id
          const isDragging = draggedTabId === tab.id
          const isDragOver = dragOverTabId === tab.id

          return (
            <div
              key={tab.id}
              className={`canvas-tab ${isActive ? 'active' : ''} ${isEditing ? 'editing' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
              onClick={() => !isEditing && onTabClick(tab.id)}
              onDoubleClick={(e) => startEditing(tab, e)}
              onContextMenu={(e) => handleContextMenu(tab, e)}
              draggable={!isEditing && onTabReorder !== undefined}
              onDragStart={(e) => handleDragStart(tab, e)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(tab, e)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(tab, e)}
            >
              {/* Tab Content */}
              <div className="canvas-tab-content">
                {isEditing ? (
                  // Editing Mode
                  <div className="canvas-tab-edit">
                    <input
                      ref={inputRef}
                      type="text"
                      className="canvas-tab-edit-input"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      onBlur={finishEditing}
                    />
                    <button
                      className="canvas-tab-edit-confirm"
                      onClick={finishEditing}
                      title="Confirm (Enter)"
                    >
                      <Check size={14} />
                    </button>
                  </div>
                ) : (
                  // Display Mode
                  <>
                    <span className="canvas-tab-name">
                      {tab.name}
                      {tab.unsavedChanges && <span className="canvas-tab-unsaved">*</span>}
                    </span>

                    {/* Actions */}
                    <div className="canvas-tab-actions">
                      <button
                        className="canvas-tab-action canvas-tab-rename"
                        onClick={(e) => startEditing(tab, e)}
                        title="Rename tab"
                      >
                        <Edit2 size={14} />
                      </button>

                      <button
                        className="canvas-tab-action canvas-tab-close"
                        onClick={(e) => handleTabClose(tab, e)}
                        disabled={tabs.length === 1}
                        title={tabs.length === 1 ? "Can't close last tab" : "Close tab"}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )
        })}

        {/* Add Tab Button - Right after tabs like Chrome */}
        <button
          className="canvas-tab-add"
          onClick={onTabAdd}
          title="Add new canvas (Ctrl+T)"
        >
          <Plus size={16} />
        </button>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="canvas-tab-context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 10001
          }}
        >
          <button
            className="context-menu-item"
            onClick={() => {
              const tab = tabs.find(t => t.id === contextMenu.tabId)
              if (tab) {
                startEditing(tab, {} as React.MouseEvent)
                closeContextMenu()
              }
            }}
          >
            <Edit2 size={14} />
            <span>Rename</span>
          </button>

          {onTabDuplicate && (
            <button
              className="context-menu-item"
              onClick={() => {
                onTabDuplicate(contextMenu.tabId)
                closeContextMenu()
              }}
            >
              <Copy size={14} />
              <span>Duplicate</span>
            </button>
          )}

          <div className="context-menu-separator" />

          <button
            className="context-menu-item"
            onClick={() => {
              const tab = tabs.find(t => t.id === contextMenu.tabId)
              if (tab) {
                handleTabClose(tab, {} as React.MouseEvent)
                closeContextMenu()
              }
            }}
            disabled={tabs.length === 1}
          >
            <X size={14} />
            <span>Close</span>
          </button>

          {onCloseOthers && tabs.length > 1 && (
            <button
              className="context-menu-item"
              onClick={() => {
                onCloseOthers(contextMenu.tabId)
                closeContextMenu()
              }}
            >
              <XCircle size={14} />
              <span>Close Others</span>
            </button>
          )}

          {onCloseToRight && (() => {
            const tabIndex = tabs.findIndex(t => t.id === contextMenu.tabId)
            const hasTabsToRight = tabIndex < tabs.length - 1
            return hasTabsToRight ? (
              <button
                className="context-menu-item"
                onClick={() => {
                  onCloseToRight(contextMenu.tabId)
                  closeContextMenu()
                }}
              >
                <ChevronsRight size={14} />
                <span>Close to the Right</span>
              </button>
            ) : null
          })()}
        </div>
      )}
    </div>
  )
}

export default memo(CanvasTabBar)
